import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import { handleLoanFeesError } from 'app/pages/loanApplication/LoanApplication.utils';
import CicReasonFormModal from 'app/shared/modules/CicReasonFormModal';
import { CIC_REASON_MODAL_CONTROLS_ID } from 'app/shared/modules/CicReasonFormModal/CicReasonFormModal.constants';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { object } from 'yup';

import { FeatureFlag } from 'constants/featureFlags';
import useForm from 'hooks/useForm';
import useIsMounted from 'hooks/useIsMounted';
import useLoanId from 'hooks/useLoanId';
import { getConditionalArray } from 'utils/data';
import { getPendingChangesInfo, getTotalAmountFee } from 'utils/loanFees';

import { openDialogAction } from 'store/global/actions';
import {
  editLoanFeesPatchAction,
  getApplicationChangesHistoryAction,
  getLoanCicFeeReasonsActionV2,
  getLoanFeeCreditsAction,
  getLoanFeeReasonHistoryAction,
  getLoanFeesSummary,
  getLoanFeesViewAction,
} from 'store/loanApplications/actions';
import { selectCicChangeReasons, selectCureBaseline } from 'store/loanApplications/selectors';
import {
  selectCicFeeReasonOptions,
  selectFeeProviderOptions,
  selectFeeTypesOptionsWithoutSellerPaid,
} from 'store/system/selectors';

import PendingChangesNumber from '../../../Label/PendingChangesNumber';
import TotalAmount from '../../../Label/TotalAmount';
import Controls from '../../Controls';
import FeeTable from '../../FeeTable';
import { getDefaultSchema, getEditFeeActionData, getFeesPatchRequest } from '../Section.utils';

import { getInitialFormValues } from './Default.utils';

const schema = object().shape({
  set_1: getDefaultSchema(),
});

const DefaultSection = ({
  cdSection,
  leSection,
  items,
  isNonBorrowerPaidFieldsIncluded,
  isWithHeader,
  labelNameAmountSection,
  isLoading,
  setEditedSection,
  setIsLoading,
  parentRef,
}) => {
  const dispatch = useDispatch();
  const loanId = useLoanId();
  const isMounted = useIsMounted();
  const { applicationId } = useParams();

  const isSFCicReasonEnabledFF = useFeatureToggle(FeatureFlag.EnableStraightforwardCicReason);

  const [{ pendingChangesNumber, isTotalAmountPending }, setPendingChangesInfo] = useState({});
  const [pendingCredits, setPendingCredits] = useState([]);

  const addPendingCredit = useCallback(credit => {
    setPendingCredits(prev => [...prev, credit]);
  }, []);

  const removePendingCredit = useCallback(creditId => {
    setPendingCredits(prev => prev.filter(credit => credit.id !== creditId));
  }, []);

  const feesOptions = useSelector(selectFeeTypesOptionsWithoutSellerPaid);
  const feeProviderOptions = useSelector(selectFeeProviderOptions);
  const cicFeeReasons = useSelector(selectCicFeeReasonOptions);
  const feesWithReason = useSelector(selectCicChangeReasons);
  const cureBaseline = useSelector(selectCureBaseline);

  const initialFormValues = getInitialFormValues({
    items,
    feesOptions,
    feeProviderOptions,
    cureBaseline,
  });

  const {
    form,
    setForm,
    handleChange,
    validationErrors = {},
    setSchema,
    setInForm,
    validateField,
    isFormChanged,
    validateForm,
    setIsTouched,
    userEditedFieldNamesRef,
  } = useForm(initialFormValues);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  useEffect(() => {
    const pendingChangesInfo = getPendingChangesInfo(parentRef);
    setPendingChangesInfo(pendingChangesInfo);
  }, [form, parentRef]);

  useEffect(() => {
    setEditedSection(prev => ({ ...prev, isFormChanged, ref: parentRef }));
  }, [setEditedSection, isFormChanged, parentRef]);

  const totalAmount = useMemo(() => _.sumBy(form.set_1, getTotalAmountFee), [form]);

  const getIsCicReasonModalDisplayed = useCallback(
    (data, isCicReasonButtonTriggered, userEditedFieldNames) => {
      const actionData = getEditFeeActionData({
        data,
        loanId,
        cicFeeReasons,
        feesWithReason,
        pendingCredits,
        cureBaseline,
        userEditedFieldNames,
        rowPaths: data.map((_fee, index) => `set_1.[${index}]`),
      });

      if (!isSFCicReasonEnabledFF || !cureBaseline) {
        return [false, actionData];
      }

      return [
        _.some(actionData.changes, i => {
          if (isCicReasonButtonTriggered) {
            return i.fee.availableReasons;
          }

          return i.fee.availableReasons && !i.fee.isIterationBPAmountEQ;
        }),
        actionData,
      ];
    },
    [cicFeeReasons, loanId, feesWithReason, cureBaseline, isSFCicReasonEnabledFF, pendingCredits],
  );

  const [isApplyCicReasonButtonDisplayed] = useMemo(
    () => getIsCicReasonModalDisplayed(initialFormValues.set_1, true),
    [getIsCicReasonModalDisplayed, initialFormValues.set_1],
  );

  const onCancelForm = useCallback(() => {
    setPendingCredits([]);
    setEditedSection(null);
  }, [setEditedSection]);

  const onSubmit = useCallback(
    async e => {
      e?.preventDefault();

      const isApplyCicReason = e?.currentTarget.dataset.isApplyCicReason === 'true';

      const hasPendingCredits = pendingCredits.length > 0;

      if (
        !(await validateForm({
          form,
          noFormChangedValidate: isApplyCicReason || hasPendingCredits,
        }))
      ) {
        return;
      }

      const [isSFCicReasonEnabled, actionData] = getIsCicReasonModalDisplayed(
        form.set_1,
        isApplyCicReason,
        userEditedFieldNamesRef.current,
      );

      const action = actionData => {
        setIsLoading(true);

        return dispatch(editLoanFeesPatchAction(getFeesPatchRequest(actionData)))
          .then(() =>
            Promise.all([
              dispatch(getLoanFeesViewAction({ applicationId })),
              dispatch(getLoanFeesSummary({ applicationId })),
              dispatch(getLoanFeeReasonHistoryAction({ loanId })),
              dispatch(getLoanFeeCreditsAction({ loanId })),
              dispatch(getLoanCicFeeReasonsActionV2({ loanId })),
              // Pending fee credits reach the people menu and the save summary through the changes history.
              ...getConditionalArray(hasPendingCredits, () => [
                dispatch(getApplicationChangesHistoryAction({ applicationId })),
              ]),
            ]),
          )
          .then(() => {
            if (isMounted) {
              setIsTouched(false);
            }

            setPendingCredits([]);
            setEditedSection(null);
          })
          .catch(({ response }) => handleLoanFeesError({ response, dispatch }))
          .finally(() => setIsLoading(false));
      };

      if (isSFCicReasonEnabled) {
        return dispatch(
          openDialogAction({
            header: 'Select CIC Reason',
            isRestyled: true,
            size: ModalSize.LARGE,
            withDivider: true,
            hasScrollY: true,
            body: dialogProps => (
              <CicReasonFormModal
                action={action}
                actionData={actionData}
                isCicReasonButtonTriggered={isApplyCicReason}
                {...dialogProps}
              />
            ),
            customButtons: <div className="w-full" id={CIC_REASON_MODAL_CONTROLS_ID} />,
          }),
        );
      }

      return action(actionData);
    },
    [
      validateForm,
      form,
      pendingCredits,
      setIsLoading,
      dispatch,
      applicationId,
      loanId,
      isMounted,
      setEditedSection,
      setIsTouched,
      getIsCicReasonModalDisplayed,
      userEditedFieldNamesRef,
    ],
  );

  useEffect(() => {
    parentRef.current.onCancelForm = onCancelForm;
    parentRef.current.onSubmit = onSubmit;
  }, [onCancelForm, onSubmit, parentRef]);

  const meta = useMemo(
    () => ({
      handleChange,
      isFormChanged,
      validateField,
      validateForm,
      setInForm,
      setForm,
      setIsTouched,
      validationErrors,
      setEditedSection,
      pendingCredits,
      addPendingCredit,
      removePendingCredit,
    }),
    [
      handleChange,
      isFormChanged,
      validateField,
      validateForm,
      setInForm,
      setForm,
      setIsTouched,
      validationErrors,
      setEditedSection,
      pendingCredits,
      addPendingCredit,
      removePendingCredit,
    ],
  );

  return (
    <>
      <FeeTable
        data={form.set_1}
        isLoading={isLoading}
        isNonBorrowerPaidFieldsIncluded={isNonBorrowerPaidFieldsIncluded}
        isWithHeader={isWithHeader}
        labelNameAmountSection={labelNameAmountSection}
        leSection={leSection}
        meta={meta}
      />

      {createPortal(
        <TotalAmount isPending={isTotalAmountPending} value={totalAmount} />,
        document.getElementById(`total-loan-fee-section-${cdSection}`),
      )}

      {createPortal(
        <PendingChangesNumber value={pendingChangesNumber} />,
        document.getElementById(`pending-changes-number-${cdSection}`),
      )}

      <Controls
        isApplyCicReasonButtonDisplayed={isApplyCicReasonButtonDisplayed}
        isFormChanged={isFormChanged || pendingCredits.length > 0}
        isLoading={isLoading}
        onCancelForm={onCancelForm}
        onSubmit={onSubmit}
      />
    </>
  );
};

DefaultSection.propTypes = {
  ...WithLoadingPropTypes,
  items: PropTypes.array.isRequired,
  isNonBorrowerPaidFieldsIncluded: PropTypes.bool.isRequired,
  isWithHeader: PropTypes.bool.isRequired,
  leSection: PropTypes.string.isRequired,
  cdSection: PropTypes.string.isRequired,
  setEditedSection: PropTypes.func.isRequired,
  parentRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  labelNameAmountSection: PropTypes.string,
};

export default DefaultSection;
